import { Component, DestroyRef, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AppSettingsService } from '@services/app-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationStart } from '@angular/router';
import { filter } from 'rxjs';
import { tuainFormsBaseModules, tuainBaseComponents } from '@components/tuain.modules';
import { SHARED_MODULES } from '@shared/shared-standalone';
import { EventManagerService } from '@services/event-manager.service';
import { ComponentPageService } from '@services/component-pages.service';
import { AppConfigService } from '@services/app-configuration.service';
import { FormOperationService } from '@services/form-manager.service';
import { activateMenuByUrl } from '@core/utils/menu.utils';
import { buildBaseUrl } from '@core/utils/url.utils';
import { defaultConfig } from '../../../../app.config';
import { environment } from 'src/environments/environment';

const { configLoaded, menuObtained, sessionEstablished, sessionEnded } = defaultConfig.appEvents;
@Component({
  standalone: true,
  selector: 'app-menu-left',
  imports: [...tuainFormsBaseModules, ...tuainBaseComponents, ...SHARED_MODULES],
  templateUrl: './menu-left.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./menu-left.component.scss'],
})
export class MenuLeftComponent {
  /** BUG-274532: URL pública de la política de privacidad (parametrizable por ambiente). */
  readonly privacyPolicyUrl =
    (environment as { privacyPolicyUrl?: string }).privacyPolicyUrl ?? defaultConfig.privatePolicy;
  /** #278572: sitio web oficial del FNA al que redirige el logo (parametrizable por ambiente). */
  readonly fnaWebsiteUrl =
    (environment as { fnaWebsiteUrl?: string }).fnaWebsiteUrl ??
    (defaultConfig as { fnaWebsiteUrl?: string }).fnaWebsiteUrl;
  private readonly _cdr = inject(ChangeDetectorRef);
  private _eventManager = inject(EventManagerService);
  private settings = inject(AppSettingsService);
  private _router = inject(Router);
  private _componentPageService = inject(ComponentPageService);
  private _appConfig = inject(AppConfigService);
  private _formOperationService = inject(FormOperationService);

  private destroyRef = inject(DestroyRef);
  menuColor: string;
  isMenuShadow: boolean;
  isMenuUnfixed: boolean;
  isSidebarOpen: boolean;
  isMobileView: boolean;
  leftMenuWidth: number;
  isMenuCollapsed: boolean;
  logo: string;
  menuData: any[];
  menuDataActivated: any[];
  role: string;
  assetsBase: string;
  headerLogo: string = buildBaseUrl((defaultConfig as any).serverEntryPoint) + '/api/branding/logo/headerLogo';
  compactLogo: string = buildBaseUrl((defaultConfig as any).serverEntryPoint) + '/api/branding/logo/compactLogo';

  constructor() {
    const userInfo = JSON.parse(localStorage.getItem('user'));
    this.role = userInfo ? userInfo.role : null;
    this._eventManager
      .getSubject(configLoaded.name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.assetsBase = this._appConfig.getParameter('assetsBase');
        this._cdr.markForCheck();
      });
    this._eventManager
      .getSubject(sessionEstablished.name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => data && this.menuInitialize());
    this._eventManager
      .getSubject(menuObtained.name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => this.buildMenu(data.mainMenu));
  }

  onHeaderLogoError(): void {
    // Si la URL del CMS falla, cae al asset empaquetado.
    this.headerLogo = '';
  }

  onCompactLogoError(): void {
    // Si la URL del CMS falla, cae al asset empaquetado.
    this.compactLogo = '';
  }

  menuInitialize() {
    this.settings.changes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(state => {
      this.menuColor = 'dark'; // state.menuColor;
      this.isMenuShadow = state.isMenuShadow;
      this.isMenuUnfixed = state.isMenuUnfixed;
      this.isSidebarOpen = state.isSidebarOpen;
      this.isMobileView = state.isMobileView;
      this.leftMenuWidth = state.leftMenuWidth;
      this.isMenuCollapsed = this.isMobileView ? false : state.isMenuCollapsed;
      this.logo = state.logo;
      this._cdr.markForCheck();
    });
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationStart) => {
        this.activateMenu(event.url ? event.url : null);
      });
  }

  buildMenu(data) {
    this.menuData = this.preprocessMenudata(data);
    this.activateMenu(this._router.url);
  }

  activateMenu(url: string) {
    this.menuDataActivated = activateMenuByUrl(url, this.menuData, true);
  }

  toggleSettings() {
    this.settings.set({ isSidebarOpen: !this.isSidebarOpen });
  }

  toggleCollapsed() {
    if (this.isMobileView) {
      return;
    }
    this.isMenuCollapsed = !this.isMenuCollapsed;
    this.onCollapse(this.isMenuCollapsed);
  }

  onCollapse(value: any) {
    this.settings.set({ isMenuCollapsed: value });
  }

  openPage(item) {
    const { formData } = item;
    if (item.form === 'logout') {
      return this._eventManager.next(sessionEnded.name, null);
    }
    const formParams: any = { name: item.form };
    if (formData) {
      const { state, subject, fields } = formData ?? {};
      state && (formParams.state = state);
      subject && (formParams.subject = subject);
      fields && (formParams.fields = fields);
    }
    this._formOperationService.openForm(null, formParams);
    if (this.isMobileView) {
      this.settings.set({ isMobileMenuOpen: false });
    }
  }

  preprocessMenudata(menuData) {
    menuData.forEach(item => {
      if (item.form) {
        let pageUrl = `/${this._componentPageService.formRoute(item.form)}`;
        item.additionalInfo =
          item.additionalInfo && !Array.isArray(item.additionalInfo) ? [item.additionalInfo] : item.additionalInfo;
        item.additionalInfo?.forEach(segment => (pageUrl += `/${segment}`));
        item.url = pageUrl;
        item.key = item.form;
      }
      if (item.children) {
        this.preprocessMenudata(item.children);
      }
    });
    return menuData;
  }
}
